import React from "react";
import styled from "styled-components";
import { Color, FontWeight, border, form, transparentize } from "@glossy";
import Icon, { Glyph } from "@ui-kit/icon";

interface CommonProps {
  onClick?: () => void;
  glyph?: Glyph;
  maxWidth?: number;
  className?: string;
}
interface LabelProps {
  label: React.ReactNode;
  hasChevron?: boolean;
}
interface GlyphProps {
  label?: never;
  hasChevron?: never;
}
export type IProps = CommonProps & (LabelProps | GlyphProps);

const Root = styled.button<Partial<IProps>>`
  display: flex;
  height: 100%;
  align-items: center;
  white-space: nowrap;
  padding: 0 24px;
  font-size: 14px;
  font-weight: ${FontWeight.Normal};
  color: ${form};
  cursor: pointer;
  background-color: transparent;
  border-right: 1px solid ${border};
  :hover {
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
  :active,
  &[aria-expanded="true"] {
    background-color: ${(props) => transparentize(0.8, form(props, "primary"))};
  }
`;

const PADDINGS = 48;
const ICON_WIDTH = 28;
const CHEVRON_WIDTH = 28;
const FADE_WIDTH = 48;

interface SCLabelProps {
  maxWidth: number;
  withChevron: boolean;
  withIcon: boolean;
}
const Label = styled.div.attrs(
  ({ maxWidth, withChevron, withIcon }: SCLabelProps) => ({
    spacingRight:
      maxWidth -
      PADDINGS -
      (withChevron ? CHEVRON_WIDTH : 0) -
      (withIcon ? ICON_WIDTH : 0),
  })
)<SCLabelProps>`
  display: inline-block;
  max-width: ${(props) => props.spacingRight}px;
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    ${Color.Black} ${(props) => props.spacingRight - FADE_WIDTH}px,
    transparent ${(props) => props.spacingRight}px
  );
`;

const Chevron = styled(Icon)`
  margin-left: 12px;
  display: inline-flex;
`;

const StyledIcon = styled(Icon)<{ isAlone: boolean }>`
  margin-right: ${(props) => !props.isAlone && "12px"};
`;

const FilterbarGiganticButton = React.forwardRef(
  (
    {
      label,
      hasChevron,
      glyph,
      maxWidth = 240,
      onClick,
      className,
      ...props
    }: IProps,
    ref: React.Ref<HTMLButtonElement>
  ) => (
    <Root onClick={onClick} ref={ref} className={className} {...props}>
      {!!glyph && (
        <StyledIcon glyph={glyph} size={label ? "s" : "l"} isAlone={!label} />
      )}
      {!!label && (
        <Label maxWidth={maxWidth} withChevron={hasChevron} withIcon={!!glyph}>
          {label}
        </Label>
      )}
      {!!hasChevron && <Chevron glyph="chevron-small-down" size="s" />}
    </Root>
  )
);

export default FilterbarGiganticButton;
